import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--cascade"
// Manages cascading comboboxes where child options depend on parent selection
//
// Usage:
//   <div data-controller="ui--cascade">
//     <%= render Ui::Combobox::Component.new(
//       name: "country_id",
//       options: @countries,
//       data: { "ui--cascade-target": "parent" }
//     ) %>
//
//     <%= render Ui::Combobox::Component.new(
//       name: "state_id",
//       disabled: true,
//       data: {
//         "ui--cascade-target": "child",
//         "ui--cascade-url-param": "/states.json"
//       }
//     ) %>
//   </div>
//
// Expected JSON response: { "options": [{ "value": "1", "label": "California" }, ...] }
// Or just an array: [{ "value": "1", "label": "California" }, ...]
//
export default class extends Controller {
  static targets = ["parent", "child"]

  connect() {
    this.boundParentChanged = this.parentChanged.bind(this)
    this.parentTarget.addEventListener("ui--combobox:change", this.boundParentChanged)
  }

  disconnect() {
    this.parentTarget.removeEventListener("ui--combobox:change", this.boundParentChanged)
  }

  parentChanged(event) {
    const { value } = event.detail
    this.childTargets.forEach(child => this.updateChild(child, value))
  }

  async updateChild(childElement, parentValue) {
    const baseUrl = childElement.dataset.uiCascadeUrlParam
    if (!baseUrl) return

    const comboboxController = this.#getComboboxController(childElement)
    if (!comboboxController) return

    // Clear current selection
    this.#clearSelection(comboboxController)

    // If parent is cleared, disable the child
    if (!parentValue) {
      this.#disableChild(comboboxController)
      return
    }

    // Enable and fetch new options
    this.#enableChild(comboboxController)
    await this.#fetchOptions(comboboxController, baseUrl, parentValue)
  }

  #getComboboxController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "ui--combobox")
  }

  #clearSelection(comboboxController) {
    comboboxController.hiddenTarget.value = ""
    comboboxController.inputTarget.value = ""
    comboboxController.updateClearButton()
  }

  #disableChild(comboboxController) {
    comboboxController.inputTarget.disabled = true
    this.#clearOptions(comboboxController)
  }

  #enableChild(comboboxController) {
    comboboxController.inputTarget.disabled = false
  }

  async #fetchOptions(comboboxController, baseUrl, parentValue) {
    // Show loading state
    if (comboboxController.hasLoadingTarget) {
      comboboxController.loadingTarget.classList.remove("hidden")
    }

    try {
      const url = this.#buildUrl(baseUrl, parentValue)
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Network response was not ok")

      const data = await response.json()
      const options = Array.isArray(data) ? data : (data.options || [])
      this.#replaceOptions(comboboxController, options)
    } catch (error) {
      console.error("Cascade fetch error:", error)
      this.#clearOptions(comboboxController)
    } finally {
      if (comboboxController.hasLoadingTarget) {
        comboboxController.loadingTarget.classList.add("hidden")
      }
    }
  }

  #buildUrl(baseUrl, parentValue) {
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("parent_id", parentValue)
    return url.toString()
  }

  #clearOptions(comboboxController) {
    comboboxController.optionTargets.forEach(opt => opt.remove())
    comboboxController.allOptions = []

    if (comboboxController.hasEmptyTarget) {
      comboboxController.emptyTarget.classList.add("hidden")
    }
  }

  #replaceOptions(comboboxController, options) {
    this.#clearOptions(comboboxController)

    options.forEach(opt => {
      const li = this.#createOptionElement(opt)
      comboboxController.listTarget.insertBefore(li, comboboxController.emptyTarget || null)

      comboboxController.allOptions.push({
        element: li,
        value: String(opt.value),
        label: (opt.label || "").toLowerCase(),
        description: (opt.description || "").toLowerCase()
      })
    })
  }

  #createOptionElement(opt) {
    const li = document.createElement("li")
    li.setAttribute("role", "option")
    li.setAttribute("aria-selected", "false")
    li.setAttribute("data-ui--combobox-target", "option")
    li.setAttribute("data-value", opt.value)
    li.setAttribute("data-label", opt.label)
    li.setAttribute("data-description", opt.description || "")
    li.setAttribute("data-action", "click->ui--combobox#select mouseenter->ui--combobox#highlight")
    li.className = "relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white group"

    const wrapper = document.createElement("div")
    wrapper.className = "flex"

    const labelSpan = document.createElement("span")
    labelSpan.className = "truncate"
    labelSpan.textContent = opt.label

    wrapper.appendChild(labelSpan)

    if (opt.description) {
      const descSpan = document.createElement("span")
      descSpan.className = "ml-2 truncate text-gray-500 group-hover:text-indigo-200"
      descSpan.textContent = opt.description
      wrapper.appendChild(descSpan)
    }

    li.appendChild(wrapper)
    return li
  }
}
